import { assign, fromPromise, setup, type AnyActorRef } from 'xstate';
import { sendCustomSuccessAlert, showAxiosErrorAction } from '~/features/actions';
import type { ServiceError } from '~/features/alerts/errors';
import { employerInfoToForm, type EmployerInfoUpsertPayload } from '~/features/employer/components';
import type { AppActorSystem } from '~/features/xstateSystem';
import type { EmployerNameUpdatedEvent } from '~/services/employer/employer.service.machine';
import type { EmployerInfo } from '~/types/hlx/employerInfo';
import { getCurrentEmployerInfo, upsertCurrentEmployerInfo } from './api';

type LoadEvent = { type: 'ON_LOAD' };
type RetryEvent = { type: 'ON_RETRY' };
type SubmitEvent = {
  type: 'ON_SUBMIT';
  payload: { data: EmployerInfoUpsertPayload };
};

type Events = LoadEvent | RetryEvent | SubmitEvent;

type Input = {
  // The market actor that owns the sidebar's employerUser; told the new name after a save.
  market?: AnyActorRef;
};

type Context = {
  market?: AnyActorRef;
  employerInfo: EmployerInfo | null;
  form: ReturnType<typeof employerInfoToForm>;
  loading: boolean;
  saving: boolean;
  error?: ServiceError | unknown;
};

export const employerInfoMachine = setup({
  types: {
    context: {} as Context,
    events: {} as Events,
  },
  actors: {
    getCurrentEmployerInfo: fromPromise(getCurrentEmployerInfo),
    upsertCurrentEmployerInfo: fromPromise(
      async ({ input }: { input: EmployerInfoUpsertPayload }) => upsertCurrentEmployerInfo(input)
    ),
  },
  actions: {
    setEmployerInfo: assign({
      employerInfo: ({ event }) => (event as unknown as { output: EmployerInfo | null }).output,
      form: ({ event }) =>
        employerInfoToForm((event as unknown as { output: EmployerInfo | null }).output),
      loading: false,
      error: undefined,
    }),
    setSavedEmployerInfo: assign({
      employerInfo: ({ event }) => (event as unknown as { output: EmployerInfo }).output,
      form: ({ event }) =>
        employerInfoToForm((event as unknown as { output: EmployerInfo }).output),
      saving: false,
      error: undefined,
    }),
    notifyEmployerName: ({ context, event }) => {
      const output = (event as unknown as { output: EmployerInfo }).output;
      if (!output?.legalEntityName) {
        return;
      }
      context.market?.send({
        type: 'ON_EMPLOYER_NAME_UPDATED',
        payload: { employerName: output.legalEntityName },
      } satisfies EmployerNameUpdatedEvent);
    },
    setLoading: assign({ loading: true, error: undefined }),
    setSaving: assign({ saving: true, error: undefined }),
    resetLoading: assign({ loading: false }),
    resetSaving: assign({ saving: false }),
    setError: assign({
      error: ({ event }) => (event as unknown as { error: unknown }).error,
    }),
    sendError: showAxiosErrorAction,
    sendSuccess: ({ system }) => {
      try {
        sendCustomSuccessAlert({
          system: system as AppActorSystem,
          text: 'Organization saved',
          subText: 'Employer info has been updated.',
        });
      } catch {
        return;
      }
    },
  },
}).createMachine({
  context: ({ input }) => ({
    market: (input as Input | undefined)?.market,
    employerInfo: null,
    form: employerInfoToForm(null),
    loading: false,
    saving: false,
    error: undefined,
  }),
  initial: '$_IDLE',
  states: {
    $_IDLE: {
      on: {
        ON_LOAD: {
          target: '$_LOADING',
          actions: 'setLoading',
        },
      },
    },
    $_LOADING: {
      invoke: {
        src: 'getCurrentEmployerInfo',
        onDone: {
          target: '$_READY',
          actions: 'setEmployerInfo',
        },
        onError: {
          target: '$_ERROR',
          actions: ['sendError', 'setError', 'resetLoading'],
        },
      },
    },
    $_READY: {
      on: {
        ON_LOAD: {
          target: '$_LOADING',
          actions: 'setLoading',
        },
        ON_SUBMIT: {
          target: '$_SAVING',
          actions: 'setSaving',
        },
      },
    },
    $_SAVING: {
      invoke: {
        src: 'upsertCurrentEmployerInfo',
        input: ({ event }) => (event as SubmitEvent).payload.data,
        onDone: {
          target: '$_READY',
          actions: ['setSavedEmployerInfo', 'notifyEmployerName', 'sendSuccess'],
        },
        onError: {
          target: '$_ERROR',
          actions: ['sendError', 'setError', 'resetSaving'],
        },
      },
    },
    $_ERROR: {
      on: {
        ON_RETRY: {
          target: '$_LOADING',
          actions: 'setLoading',
        },
        ON_LOAD: {
          target: '$_LOADING',
          actions: 'setLoading',
        },
      },
    },
  },
});
